import { AgendaConfiguration } from '@/api/agendaConfiguration';
import { Booking } from '@/api/booking';
import { Anchor, Button, Card, Loader, Skeleton, Stack, Text, Title, Tooltip } from '@mantine/core';

import classes from '@/pages/saveBookingPage.module.css';
import { useTranslation } from 'react-i18next';
import { secondsToTimeFormat } from '@/utils/availability';
import { useMediaQuery } from '@mantine/hooks';
import { LinkifyText } from '../LinkifyText/LinkifyText';
import { useCallback, useState } from 'react';
import { useError } from '@/contexts/ErrorContext';
import { ApiError, useEloApi } from '@/api/api';
import { IconAlertCircleFilled, IconArrowNarrowRight, IconCopy } from '@tabler/icons-react';
import { User } from '@/api/users';

interface SaveBookingEndProps {
  receivedBooking: Booking | null;
  agendaConfiguration: AgendaConfiguration;
  localUser: User;
  isOnSite: boolean;
  extension: string;
}

function convertToAmPm(time: string): string {
  const date = new Date(time);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${ampm}`;
}

export const SaveBookingEnd = ({
  receivedBooking,
  agendaConfiguration,
  localUser,
  isOnSite,
  extension,
}: SaveBookingEndProps) => {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [loadingGetLink, setLoadingGetLink] = useState(false);

  const api = useEloApi({});
  const errorContext = useError();
  const getPaymentLink = useCallback(async () => {
    if (!receivedBooking) return;
    setLoadingGetLink(true);
    try {
      let paymentLink = '';
      if (receivedBooking.stripePaymentLink) {
        paymentLink = receivedBooking.stripePaymentLink;
      } else {
        const res = await api.getPaymentLink(receivedBooking.id);
        paymentLink = res.url;
      }
      if (paymentLink) {
        new URL(paymentLink);
        window.open(paymentLink, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ApiError) {
        errorContext.setError(error.message);
      } else {
        throw error;
      }
    } finally {
      setLoadingGetLink(false);
    }
  }, [receivedBooking, api, errorContext]);

  const [copyMessage, setCopyMessage] = useState('Haz click para copiar');
  const handleTextClick = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopyMessage('Texto copiado');
        setTimeout(() => {
          setCopyMessage('Haz click para copiar');
        }, 800);
      })
      .catch((err) => console.error('Error al copiar texto:', err));
  };

  return receivedBooking ? (
    <Card shadow="sm" padding="lg" radius="md" withBorder className={classes.cardTree}>
      {agendaConfiguration.paymentOption === 2 && !receivedBooking.paid && (
        <div
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            zIndex: 11,
            display: 'flex',
          }}
        >
          <div style={{ display: 'flex', margin: 'auto' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                margin: 'auto',
              }}
            >
              <Loader style={{ margin: 'auto' }} size="lg"></Loader>
              <Title className={classes.loadingDots} style={{ textAlign: 'center' }} order={3}>
                Procesando pago
              </Title>
              <Text size="sm">El proceso puede tomar varios minutos</Text>
            </div>
          </div>
        </div>
      )}
      <div
        style={{
          position: 'relative',
        }}
      >
        <Skeleton visible={agendaConfiguration.paymentOption === 2 && !receivedBooking.paid}>
          <Title className={classes.finalTitle}>{t('save_booking_page.see_data.title')}</Title>
        </Skeleton>

        <Skeleton visible={agendaConfiguration.paymentOption === 2 && !receivedBooking.paid}>
          <div
            style={{
              fontSize: '0.85rem',
              fontWeight: '700',
              color: 'currentColor',
              marginTop: '13px',
            }}
          >
            <span
              style={{
                background: 'rgba(34, 139, 230, 0.2)',
                borderRadius: '0.9rem',
                padding: '3px 8px',
              }}
            >
              {(receivedBooking?.startDate &&
                convertToAmPm(new Date(receivedBooking.startDate).toISOString())) ||
                ''}
            </span>
            {' - '}
            <span
              style={{
                background: 'rgba(34, 139, 230, 0.2)',
                borderRadius: '0.9rem',
                padding: '3px 8px',
              }}
            >
              {(receivedBooking?.startDate &&
                receivedBooking?.duration &&
                convertToAmPm(
                  new Date(
                    new Date(receivedBooking.startDate).getTime() + receivedBooking.duration * 1000
                  ).toISOString()
                )) ||
                ''}
            </span>
          </div>
        </Skeleton>
      </div>
      <Skeleton visible={agendaConfiguration.paymentOption === 2 && !receivedBooking.paid}>
        <div>
          <Text>
            <strong>{t('save_booking_page.see_data.patient')}</strong>
            {receivedBooking.name}
          </Text>
          <Text>
            <strong>{t('save_booking_page.see_data.email')}</strong>
            <Text
              style={{
                display: `${isMobile ? 'block' : 'inline'}`,
                wordBreak: 'break-word',
              }}
            >
              {receivedBooking.email}
            </Text>
          </Text>
          <Text>
            <strong>{t('save_booking_page.see_data.phone')}</strong>
            {receivedBooking.phone}
          </Text>
          <Text>
            <strong>{t('save_booking_page.see_data.day')}</strong>
            {`${new Date(receivedBooking.startDate).toLocaleDateString(i18n.language, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}`}
          </Text>
          <Text>
            <strong>{t('save_booking_page.see_data.duration')}</strong>
            {secondsToTimeFormat(receivedBooking.duration)}
          </Text>
          {isOnSite && (
            <>
              <Title order={5} style={{ marginTop: '20px' }}>
                {t('save_booking_page.see_data.location')}
              </Title>
              <Text style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                <LinkifyText
                  text={
                    agendaConfiguration.overwriteLocation
                      ? agendaConfiguration?.locationDetails ?? ''
                      : localUser?.locationDetails ?? ''
                  }
                />
              </Text>
            </>
          )}

          {agendaConfiguration.paymentOption === 2 && (
            <Text>
              <strong>{t('save_booking_page.see_data.paid')}</strong>
              <span
                style={{
                  color: `${receivedBooking.paid ? 'var(--mantine-color-green-filled)' : 'currentColor'}`,
                  fontWeight: `${receivedBooking.paid ? 'bold' : 'normal'}`,
                }}
              >
                {t(`save_booking_page.see_data.${receivedBooking.paid ? 'is_paid' : 'not_paid'}`)}
              </span>
            </Text>
          )}
          {receivedBooking.meetingUrl && !isOnSite && (
            <div>
              <Title order={5} style={{ marginTop: '20px' }}>
                {t('save_booking_page.see_data.meeting_url')}
              </Title>

              <Anchor
                style={{
                  textOverflow: 'clip',
                  wordBreak: 'break-all',
                }}
                href={receivedBooking.meetingUrl}
              >
                {receivedBooking.meetingUrl}
              </Anchor>
            </div>
          )}
          {agendaConfiguration.paymentOption !== 2 &&
            agendaConfiguration.showPaymentLink &&
            !receivedBooking.paid &&
            receivedBooking.stripePaymentLink && (
              <Stack align="flex-start" style={{ marginTop: '20px' }} gap={3}>
                {agendaConfiguration?.paymentPolicy && (
                  <>
                    <Text size="md" fw={700} style={{ width: '100%' }}>
                      Politica de pago
                    </Text>
                    <Text style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                      {agendaConfiguration.paymentPolicy}
                    </Text>
                  </>
                )}
                <Button
                  fullWidth={isMobile}
                  size="sm"
                  variant="outline"
                  style={{ marginTop: '25px' }}
                  onClick={() => {
                    getPaymentLink();
                  }}
                  loading={loadingGetLink}
                >
                  Pagar la sesión
                </Button>
              </Stack>
            )}
        </div>
      </Skeleton>

      <Skeleton visible={agendaConfiguration.paymentOption === 2 && !receivedBooking.paid}>
        <div
          style={{
            position: 'relative',
            marginTop: 'auto',
            paddingBottom: '12px',
          }}
        >
          <Title order={6}>{t('save_booking_page.see_data.link_message')}</Title>
          <IconArrowNarrowRight
            size={20}
            style={{
              position: 'absolute',
              bottom: '-0.5rem',
              left: '0',
            }}
          />
        </div>
      </Skeleton>

      <div style={{ marginTop: '-32px' }}>
        <Skeleton visible={agendaConfiguration.paymentOption === 2 && !receivedBooking.paid}>
          <Anchor
            size="sm"
            style={{
              paddingLeft: '23px',
              wordBreak: 'break-all',
              zIndex: 10,
            }}
            href={`${window.location.origin}/booking/${receivedBooking.bookingUrl}`}
          >
            {`${window.location.origin}/booking/${receivedBooking.bookingUrl}`}
          </Anchor>
        </Skeleton>
      </div>
    </Card>
  ) : (
    <Card shadow="sm" padding="lg" radius="md" withBorder className={classes.cardTree}>
      <Stack justify="center" align="center" style={{ margin: 'auto' }}>
        <IconAlertCircleFilled size={128} color="var(--mantine-color-red-7)" />
        <Title order={2}>ERROR</Title>
        <Text size="sm">{t('save_booking_page.see_data.error.error_message')}</Text>
        <Text>{t('save_booking_page.see_data.error.custom_message')}</Text>
        <Text>
          <span style={{ fontWeight: 'bold' }}>
            {t('save_booking_page.see_data.error.tech_support')}
          </span>{' '}
          <span
            style={{
              borderRadius: '2px',
              display: 'inline-block',
              width: 'fit-content',
              minWidth: '50px',
              padding: '4px 1px',
              fontSize: '13px',
              color: '#3b82f6',
              backgroundColor: 'transparent',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              userSelect: 'all',
              position: 'relative',
            }}
          >
            <Anchor href={`mailto:${t('save_booking_page.see_data.error.tech_support_email')}`}>
              {t('save_booking_page.see_data.error.tech_support_email')}
            </Anchor>
            <Tooltip label={copyMessage}>
              <IconCopy
                size={16}
                className={classes.copyIcon}
                style={{
                  position: 'absolute',
                  top: 'calc(50% + 2px)',
                  left: 'calc(100% + 8px)',
                  transform: 'translateY(-50%)',
                }}
                onClick={() =>
                  handleTextClick(t('save_booking_page.see_data.error.tech_support_email'))
                }
              />
            </Tooltip>
          </span>
        </Text>
      </Stack>
    </Card>
  );
};
